import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, message, Popconfirm, Tooltip, Dropdown, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined, EyeOutlined, EyeInvisibleOutlined, CopyOutlined, UnorderedListOutlined, MoreOutlined, AppstoreOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiMethods, App } from '../api';
import type { MenuProps } from 'antd';

export default function Apps() {
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [form] = Form.useForm();
  const [visibleApiKeys, setVisibleApiKeys] = useState<Record<number, boolean>>({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const { data } = await apiMethods.getApps();
      setApps(data.apps || []);
    } catch (error: any) {
      message.error(error.response?.data?.error || '获取应用列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingApp(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (app: App) => {
    setEditingApp(app);
    form.setFieldsValue(app);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiMethods.deleteApp(id);
      message.success('删除成功');
      fetchApps();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  const handleRegenerateKey = async (id: number) => {
    try {
      await apiMethods.regenerateApiKey(id);
      message.success('API Key 已重置，可点击眼睛图标查看');
      fetchApps();
    } catch (error: any) {
      message.error(error.response?.data?.error || '生成失败');
    }
  };

  const toggleApiKeyVisibility = (id: number) => {
    setVisibleApiKeys(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const copyApiKey = (key: string) => {
    navigator.clipboard.writeText(key);
    message.success('API Key 已复制到剪贴板');
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingApp) {
        await apiMethods.updateApp(editingApp.id, values);
        message.success('更新成功');
      } else {
        await apiMethods.createApp(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchApps();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  // 移动端操作菜单
  const getActionMenu = (record: App): MenuProps => ({
    items: [
      {
        key: 'versions',
        label: '版本管理',
        icon: <UnorderedListOutlined />,
        onClick: () => navigate(`/apps/${record.id}/versions`),
      },
      {
        key: 'edit',
        label: '编辑',
        icon: <EditOutlined />,
        onClick: () => handleEdit(record),
      },
      {
        key: 'regenerate',
        label: '重置密钥',
        icon: <KeyOutlined />,
        onClick: () => handleRegenerateKey(record.id),
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: '删除',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: '确定删除？',
            content: `确定要删除应用 "${record.display_name}" 吗？`,
            okText: '确定',
            cancelText: '取消',
            okButtonProps: { danger: true },
            onOk: () => handleDelete(record.id),
          });
        },
      },
    ],
  });

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80, responsive: ['lg'] as ['lg'] },
    { title: '应用名称', dataIndex: 'name', width: 180, responsive: ['md'] as ['md'] },
    { title: '显示名称', dataIndex: 'display_name', width: 180 },
    {
      title: '类型',
      dataIndex: 'type',
      width: 100,
      render: (type: string) => {
        if (type === 'modpack') {
          return <Tag icon={<FolderOpenOutlined />} color="purple">整合包</Tag>;
        }
        return <Tag icon={<AppstoreOutlined />} color="blue">普通</Tag>;
      },
    },
    {
      title: 'API Key',
      dataIndex: 'api_key',
      width: 320,
      render: (key: string, record: App) => {
        if (!key) return '-';
        const isVisible = visibleApiKeys[record.id];
        const displayKey = isVisible ? key : `${key.substring(0, 16)}...`;

        return (
          <Space size="small">
            <span style={{ fontFamily: 'monospace', fontSize: isMobile ? '11px' : '12px' }}>{displayKey}</span>
            <Tooltip title={isVisible ? '隐藏' : '显示'}>
              <Button
                type="text"
                size="small"
                icon={isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                onClick={() => toggleApiKeyVisibility(record.id)}
              />
            </Tooltip>
            <Tooltip title="复制">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyApiKey(key)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    { title: '创建时间', dataIndex: 'created_at', width: 200, responsive: ['lg'] as ['lg'] },
    {
      title: '操作',
      key: 'action',
      width: 300,
      fixed: isMobile ? ('right' as const) : undefined,
      render: (_: any, record: App) => {
        if (isMobile) {
          return (
            <Dropdown menu={getActionMenu(record)} trigger={['click']}>
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          );
        }
        return (
          <Space>
            <Button type="link" icon={<UnorderedListOutlined />} onClick={() => navigate(`/apps/${record.id}/versions`)}>
              版本管理
            </Button>
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              编辑
            </Button>
            <Button type="link" icon={<KeyOutlined />} onClick={() => handleRegenerateKey(record.id)}>
              重置密钥
            </Button>
            <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          添加应用
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={apps}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingApp ? '编辑应用' : '添加应用'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
        styles={isMobile ? { body: { height: 'calc(100vh - 108px)', overflowY: 'auto' } } : undefined}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="应用名称"
            rules={[{ required: true, message: '请输入应用名称' }]}
          >
            <Input placeholder="例如: myapp" />
          </Form.Item>
          <Form.Item
            name="display_name"
            label="显示名称"
            rules={[{ required: true, message: '请输入显示名称' }]}
          >
            <Input placeholder="例如: 我的应用" />
          </Form.Item>
          <Form.Item
            name="type"
            label="应用类型"
            rules={[{ required: true, message: '请选择应用类型' }]}
            tooltip="整合包类型只能上传 zip 文件，会自动解压并支持增量更新"
          >
            <Select placeholder="请选择应用类型">
              <Select.Option value="normal">
                <Space>
                  <AppstoreOutlined />
                  <span>普通应用</span>
                  <span style={{ color: '#999' }}> - 上传单个文件（exe、msi 等）</span>
                </Space>
              </Select.Option>
              <Select.Option value="modpack">
                <Space>
                  <FolderOpenOutlined />
                  <span>整合包</span>
                  <span style={{ color: '#999' }}> - 上传 zip，自动解压，支持增量更新</span>
                </Space>
              </Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
